/**
 * @file Icones d'interface en SVG genere (remplacent les emojis systeme, dont le rendu change selon l'OS) : meme style que
 * `icons.ts` (contour violet nuit epais, 2 tons, un reflet blanc), carre 32x32, aucun fichier image.
 */
const NS = 'http://www.w3.org/2000/svg';
const STROKE = '#1f1638';

type SvgAttrs = Record<string, string | number>;

const s = (tag: string, attrs: SvgAttrs = {}): SVGElement => {
  const e = document.createElementNS(NS, tag);
  for (const [k, v] of Object.entries(attrs)) e.setAttribute(k, String(v));
  return e;
};

const o = (fill: string, extra: SvgAttrs = {}): SvgAttrs => ({ fill, stroke: STROKE, 'stroke-width': 2.4, 'stroke-linejoin': 'round', 'stroke-linecap': 'round', ...extra });
/** Reflet (petit trait blanc semi-opaque) : donne le cote « brillant » des icones casual. */
const shine = (d: string): SVGElement => s('path', { d, fill: 'none', stroke: '#ffffff', 'stroke-width': 2, 'stroke-linecap': 'round', opacity: 0.75 });
const line = (d: string, w = 2.2): SVGElement => s('path', { d, fill: 'none', stroke: STROKE, 'stroke-width': w, 'stroke-linecap': 'round', 'stroke-linejoin': 'round' });

export type UiIconId =
  | 'gun' | 'gift' | 'hub' | 'gear' | 'compass' | 'cat' | 'rebirth' | 'missions' | 'map' | 'power' | 'speed' | 'money'
  | 'muscle' | 'bolt' | 'lock' | 'leaf' | 'zoomIn' | 'zoomOut' | 'reset' | 'firework' | 'sparkle' | 'bag'
  // tickets, minuteur, aucun, fusion
  | 'ticket' | 'hourglass' | 'none' | 'arrowUp'
  // modes de jeu
  | 'tree' | 'stopwatch' | 'pickaxe' | 'boss' | 'monster' | 'dragon' | 'fish' | 'log'
  // particules d'aura
  | 'star' | 'heart' | 'flame' | 'snow' | 'petal' | 'note' | 'bubble'
  // comportements d'arme
  | 'dot' | 'pierce' | 'shotgun' | 'missile' | 'comet'
  // social / en ligne
  | 'globe' | 'chat' | 'emote' | 'people' | 'trophy' | 'mail' | 'crown';

const star = (cx: number, cy: number, r: number, fill: string): SVGElement => {
  const pts: string[] = [];
  for (let i = 0; i < 10; i++) {
    const a = -Math.PI / 2 + (i * Math.PI) / 5;
    const rr = i % 2 === 0 ? r : r * 0.45;
    pts.push(`${(cx + Math.cos(a) * rr).toFixed(1)},${(cy + Math.sin(a) * rr).toFixed(1)}`);
  }
  return s('polygon', o(fill, { points: pts.join(' ') }));
};

const loupe = (sign: '+' | '-'): SVGElement[] => [
  line('M20 20l7 7', 4.5),
  s('circle', o('#bfeaff', { cx: 13.5, cy: 13.5, r: 9 })),
  line(sign === '+' ? 'M9.5 13.5h8M13.5 9.5v8' : 'M9.5 13.5h8'),
  shine('M8.5 10.5a6 6 0 0 1 3-3'),
];

const SHAPES: Record<UiIconId, () => SVGElement[]> = {
  gun: () => [
    s('rect', o('#ff9a2e', { x: 3, y: 9, width: 21, height: 8, rx: 2.5 })),
    s('rect', o('#ffc83d', { x: 22, y: 10.5, width: 7, height: 5, rx: 1.5 })),
    s('path', o('#e0761c', { d: 'M7 17h7l-2 9H6z' })),
    s('circle', { cx: 13, cy: 13, r: 1.8, fill: STROKE }),
    shine('M6 11.5h12'),
  ],
  gift: () => [
    s('rect', o('#ff4f7b', { x: 4, y: 13, width: 24, height: 15, rx: 2.5 })),
    s('rect', o('#ff7b9c', { x: 2.5, y: 8.5, width: 27, height: 6, rx: 2 })),
    s('rect', o('#ffc83d', { x: 13.5, y: 8.5, width: 5, height: 19.5 })),
    s('path', o('#ffc83d', { d: 'M16 8.5c-4-6-10-4-7 0zM16 8.5c4-6 10-4 7 0z' })),
    shine('M6 17v7'),
  ],
  hub: () => [
    s('rect', o('#fff4dc', { x: 6, y: 14, width: 20, height: 14, rx: 1.5 })),
    s('path', o('#ff6b5b', { d: 'M3 15L16 4l13 11z' })),
    s('rect', o('#b26bff', { x: 13, y: 19, width: 6, height: 9, rx: 1 })),
    s('rect', o('#3dd6ff', { x: 8.5, y: 17, width: 3.5, height: 3.5, rx: 0.6 })),
    s('rect', o('#3dd6ff', { x: 20, y: 17, width: 3.5, height: 3.5, rx: 0.6 })),
    shine('M8 13l7.5-6'),
  ],
  gear: () => {
    const teeth: SVGElement[] = [];
    for (let i = 0; i < 8; i++) {
      const a = (i * Math.PI) / 4;
      teeth.push(s('rect', o('#9aa7c7', { x: 13, y: 1.5, width: 6, height: 7, rx: 1.5, transform: `rotate(${(a * 180) / Math.PI} 16 16)` })));
    }
    return [...teeth, s('circle', o('#b9c4e0', { cx: 16, cy: 16, r: 10 })), s('circle', o('#2a2350', { cx: 16, cy: 16, r: 4 })), shine('M10 12a7 7 0 0 1 4-4')];
  },
  compass: () => [
    s('circle', o('#fff4dc', { cx: 16, cy: 16, r: 12.5 })),
    s('polygon', o('#ff4f7b', { points: '16,5 19.5,16 12.5,16' })),
    s('polygon', o('#3dd6ff', { points: '16,27 19.5,16 12.5,16' })),
    s('circle', { cx: 16, cy: 16, r: 2, fill: STROKE }),
    shine('M8.5 11a9 9 0 0 1 4-4.5'),
  ],
  cat: () => [
    s('path', o('#ff9a2e', { d: 'M5 27c0-8 2-12 2-12L6 4l7 6h6l7-6-1 11s2 4 2 12z' })),
    s('ellipse', { cx: 11.5, cy: 18, rx: 1.8, ry: 2.4, fill: STROKE }),
    s('ellipse', { cx: 20.5, cy: 18, rx: 1.8, ry: 2.4, fill: STROKE }),
    s('path', o('#ff8fa8', { d: 'M14.5 22h3l-1.5 1.8z', 'stroke-width': 1.4 })),
    shine('M8.5 9l2.5 2.2'),
  ],
  rebirth: () => [star(16, 16, 13, '#ffc83d'), star(25, 6, 4.5, '#fff4dc'), shine('M11 12l3-4')],
  missions: () => [
    s('rect', o('#fff4dc', { x: 6, y: 5, width: 20, height: 24, rx: 2.5 })),
    s('rect', o('#b26bff', { x: 11, y: 2.5, width: 10, height: 5, rx: 1.5 })),
    line('M10 14l2 2 3-4M18 14h5M10 21l2 2 3-4M18 21h5', 2),
  ],
  map: () => [
    s('path', o('#7cf05a', { d: 'M3 7l8-3 10 3 8-3v21l-8 3-10-3-8 3z' })),
    line('M11 4v21M21 7v21', 1.6),
    s('path', { d: 'M6 20c4-1 6-6 10-5s5 4 9 0', fill: 'none', stroke: '#ff4f7b', 'stroke-width': 2, 'stroke-dasharray': '2 2.5', 'stroke-linecap': 'round' }),
  ],
  power: () => [
    s('polygon', o('#ff6a3d', { points: '16,2 19.5,10 28,7 23,14.5 30,19 21.5,20 23,29 16,23 9,29 10.5,20 2,19 9,14.5 4,7 12.5,10' })),
    s('circle', o('#ffc83d', { cx: 16, cy: 16, r: 5, 'stroke-width': 1.6 })),
  ],
  speed: () => [s('polygon', o('#ffc83d', { points: '19,2 6,18 14,18 11,30 26,12 17.5,12' })), shine('M16 6.5l-6 7.5')],
  money: () => [
    s('rect', o('#7cf05a', { x: 2.5, y: 7, width: 27, height: 18, rx: 4 })),
    s('circle', o('#d6f7c9', { cx: 16, cy: 16, r: 5.2 })),
    s('circle', { cx: 7, cy: 16, r: 1.6, fill: STROKE }),
    s('circle', { cx: 25, cy: 16, r: 1.6, fill: STROKE }),
  ],
  muscle: () => [
    s('path', o('#ff6a3d', { d: 'M5 26c-2-6 0-13 5-17l3-5 5 2-2 5c5 0 10 3 11 8 1 6-4 9-10 8z' })),
    line('M15 15c2 1 3 3 3 5', 1.8),
    shine('M9 12l2.5-3'),
  ],
  bolt: () => [s('polygon', o('#3dd6ff', { points: '19,2 6,18 14,18 11,30 26,12 17.5,12' })), shine('M16 6.5l-6 7.5')],
  lock: () => [
    s('path', { d: 'M10 14v-4a6 6 0 0 1 12 0v4', fill: 'none', stroke: STROKE, 'stroke-width': 3.5, 'stroke-linecap': 'round' }),
    s('rect', o('#ffc83d', { x: 6, y: 13.5, width: 20, height: 15, rx: 3 })),
    s('circle', { cx: 16, cy: 20, r: 2.2, fill: STROKE }),
    line('M16 21v3.5', 2.4),
  ],
  leaf: () => [s('path', o('#7cf05a', { d: 'M5 27C5 13 13 5 28 4c0 15-8 23-23 23z' })), line('M5 27L20 12', 1.8), shine('M12 13c2-3 5-5 9-6')],
  zoomIn: () => loupe('+'),
  zoomOut: () => loupe('-'),
  reset: () => [
    s('path', { d: 'M25 16a9 9 0 1 1-3-6.7', fill: 'none', stroke: STROKE, 'stroke-width': 3.6, 'stroke-linecap': 'round' }),
    s('polygon', o('#ffc83d', { points: '26,3 27,12 18.5,10.5', 'stroke-width': 1.8 })),
  ],
  firework: () => {
    const rays: SVGElement[] = [];
    const cols = ['#ff4f7b', '#ffc83d', '#3dd6ff', '#b26bff'];
    for (let i = 0; i < 8; i++) {
      const a = (i * Math.PI) / 4;
      const x = 16 + Math.cos(a) * 12;
      const y = 14 + Math.sin(a) * 12;
      rays.push(s('path', { d: `M${(16 + Math.cos(a) * 4).toFixed(1)} ${(14 + Math.sin(a) * 4).toFixed(1)}L${x.toFixed(1)} ${y.toFixed(1)}`, stroke: cols[i % 4] ?? '#ffc83d', 'stroke-width': 3, 'stroke-linecap': 'round' }));
    }
    return [...rays, s('circle', o('#fff4dc', { cx: 16, cy: 14, r: 3.2, 'stroke-width': 1.8 }))];
  },
  sparkle: () => [s('path', o('#ffc83d', { d: 'M14 3c1 7 3 9 10 10-7 1-9 3-10 10-1-7-3-9-10-10 7-1 9-3 10-10z' })), s('path', o('#b26bff', { d: 'M24 18c.6 3.5 1.5 4.4 5 5-3.5.6-4.4 1.5-5 5-.6-3.5-1.5-4.4-5-5 3.5-.6 4.4-1.5 5-5z', 'stroke-width': 1.8 }))],
  bag: () => [
    s('path', { d: 'M11 11V8a5 5 0 0 1 10 0v3', fill: 'none', stroke: STROKE, 'stroke-width': 2.8, 'stroke-linecap': 'round' }),
    s('path', o('#ff4f7b', { d: 'M5 10h22l-1.5 18h-19z' })),
    s('circle', { cx: 11, cy: 14, r: 1.5, fill: STROKE }),
    s('circle', { cx: 21, cy: 14, r: 1.5, fill: STROKE }),
    shine('M8.5 13.5l-.8 10'),
  ],

  // ---- tickets, minuteur, aucun, fusion
  ticket: () => [
    s('path', o('#ff6b8a', { d: 'M3 9h26v4a3 3 0 0 0 0 6v4H3v-4a3 3 0 0 0 0-6z' })),
    s('path', { d: 'M21 10.5v11', fill: 'none', stroke: STROKE, 'stroke-width': 1.8, 'stroke-dasharray': '2 2', 'stroke-linecap': 'round' }),
    star(12, 16, 4.6, '#ffc83d'),
    shine('M6 11.5h10'),
  ],
  hourglass: () => [
    s('path', o('#bfeaff', { d: 'M9 5h14c0 6-5 8-5 11s5 5 5 11H9c0-6 5-8 5-11S9 11 9 5z' })),
    s('path', { d: 'M12 26h8c-1-3-3-4.5-4-4.5S13 23 12 26zM13 9.5h6c-.8 2-2 3-3 3.5-1-.5-2.2-1.5-3-3.5z', fill: '#ffc83d' }),
    s('rect', o('#b26bff', { x: 6, y: 2.5, width: 20, height: 4, rx: 1.5 })),
    s('rect', o('#b26bff', { x: 6, y: 25.5, width: 20, height: 4, rx: 1.5 })),
  ],
  none: () => [
    s('circle', { cx: 16, cy: 16, r: 11.5, fill: '#ffffff', stroke: STROKE, 'stroke-width': 6.5 }),
    s('circle', { cx: 16, cy: 16, r: 11.5, fill: 'none', stroke: '#ff4f7b', 'stroke-width': 3.2 }),
    s('path', { d: 'M8 24L24 8', stroke: STROKE, 'stroke-width': 6.5, 'stroke-linecap': 'round' }),
    s('path', { d: 'M8 24L24 8', stroke: '#ff4f7b', 'stroke-width': 3.2, 'stroke-linecap': 'round' }),
  ],
  arrowUp: () => [s('polygon', o('#7cf05a', { points: '16,3 28,16 21,16 21,29 11,29 11,16 4,16' })), shine('M16 7.5l-6.5 7')],

  // ---- modes de jeu
  tree: () => [
    s('rect', o('#8a5a32', { x: 13.5, y: 21, width: 5, height: 8, rx: 1 })),
    s('polygon', o('#157a5e', { points: '16,8 28,23 4,23' })),
    s('polygon', o('#1f9a76', { points: '16,2 25,14 7,14' })),
    shine('M14 6l-3.5 5'),
  ],
  stopwatch: () => [
    s('rect', o('#ff6a3d', { x: 12.5, y: 1.5, width: 7, height: 4.5, rx: 1.5 })),
    s('rect', o('#ff6a3d', { x: 23, y: 6, width: 4, height: 4, rx: 1, transform: 'rotate(45 25 8)' })),
    s('circle', o('#fff4dc', { cx: 16, cy: 18, r: 11 })),
    line('M16 18V11M16 18l4.5 2.5', 2.6),
    s('circle', { cx: 16, cy: 18, r: 1.8, fill: STROKE }),
    shine('M9 14a8 8 0 0 1 3.5-4'),
  ],
  pickaxe: () => [
    s('path', o('#b07a45', { d: 'M5.5 28.5l-2-2L19 11l2 2z' })),
    s('path', o('#b9c4e0', { d: 'M4 12C9 4.5 19.5 3 28 9c-6-1-11.5.5-15.5 4.5l-1.5-1.5-1.5 1.5C8 12 6 11.5 4 12z' })),
    shine('M10 8c4-2.5 9-3 13-1.5'),
  ],
  boss: () => [
    s('polygon', o('#fff4dc', { points: '8,13 4,2 13,8' })),
    s('polygon', o('#fff4dc', { points: '24,13 28,2 19,8' })),
    s('circle', o('#ff4f5e', { cx: 16, cy: 18, r: 11 })),
    s('path', { d: 'M9 15l5 2-1 2.5zM23 15l-5 2 1 2.5z', fill: STROKE, stroke: STROKE, 'stroke-width': 1.2, 'stroke-linejoin': 'round' }),
    s('path', o('#2a2350', { d: 'M10 23h12c-1 3-3.5 4.5-6 4.5S11 26 10 23z', 'stroke-width': 1.6 })),
    s('path', { d: 'M12 23l1.5 2.5L15 23zM17 23l1.5 2.5L20 23z', fill: '#ffffff' }),
    shine('M9.5 13a8 8 0 0 1 3-3'),
  ],
  monster: () => [
    s('path', o('#b26bff', { d: 'M8 7l3 4h10l3-4 2.5 2-2 4H28v8h-3v4h-4v-3H11v3H7v-4H4v-8h3.5l-2-4z' })),
    s('rect', { x: 10, y: 14, width: 4, height: 4, rx: 1, fill: '#ffffff', stroke: STROKE, 'stroke-width': 1.6 }),
    s('rect', { x: 18, y: 14, width: 4, height: 4, rx: 1, fill: '#ffffff', stroke: STROKE, 'stroke-width': 1.6 }),
    s('rect', { x: 11.5, y: 15.5, width: 2, height: 2, fill: STROKE }),
    s('rect', { x: 19.5, y: 15.5, width: 2, height: 2, fill: STROKE }),
  ],
  dragon: () => [
    s('polygon', o('#ffc83d', { points: '9,9 4,2 12.5,6' })),
    s('polygon', o('#ffc83d', { points: '23,9 28,2 19.5,6' })),
    s('path', o('#4fd17a', { d: 'M6 15c0-6 4-9.5 10-9.5S26 9 26 15v5c0 4.5-4 7.5-10 7.5S6 24.5 6 20z' })),
    s('ellipse', o('#ffe14d', { cx: 11.5, cy: 15, rx: 2.6, ry: 2.2, 'stroke-width': 1.6 })),
    s('ellipse', o('#ffe14d', { cx: 20.5, cy: 15, rx: 2.6, ry: 2.2, 'stroke-width': 1.6 })),
    s('path', { d: 'M11.5 13.5v3M20.5 13.5v3', stroke: STROKE, 'stroke-width': 1.6, 'stroke-linecap': 'round' }),
    s('ellipse', o('#9ff0b8', { cx: 16, cy: 22.5, rx: 6, ry: 3.6, 'stroke-width': 1.8 })),
    s('circle', { cx: 13.8, cy: 22, r: 1, fill: STROKE }),
    s('circle', { cx: 18.2, cy: 22, r: 1, fill: STROKE }),
  ],
  fish: () => [
    s('polygon', o('#ff9a2e', { points: '21,16 30,8.5 30,23.5' })),
    s('ellipse', o('#3dd6ff', { cx: 13.5, cy: 16, rx: 10.5, ry: 7.5 })),
    s('path', { d: 'M15 9.5c2 2 2 11 0 13', fill: 'none', stroke: STROKE, 'stroke-width': 1.6, 'stroke-linecap': 'round' }),
    s('circle', { cx: 8, cy: 14.5, r: 1.7, fill: STROKE }),
    shine('M6 11.5a9 6 0 0 1 5-2.5'),
  ],
  log: () => [
    s('path', o('#b07a45', { d: 'M7 9.5h18a4.5 6.5 0 0 1 0 13H7a4.5 6.5 0 0 1 0-13z' })),
    s('ellipse', o('#e9c48f', { cx: 25, cy: 16, rx: 4.5, ry: 6.5 })),
    s('ellipse', { cx: 25, cy: 16, rx: 2, ry: 3, fill: 'none', stroke: '#b07a45', 'stroke-width': 1.5 }),
    line('M9 13.5h9M7 18.5h11', 1.5),
    s('path', o('#7cf05a', { d: 'M13 9.5c-1-4 1-6.5 5-7-.5 3.5-2 6-5 7z', 'stroke-width': 1.6 })),
  ],

  // ---- particules d'aura
  star: () => [star(16, 16, 13.5, '#ffc83d'), shine('M12 11l2.5-4')],
  heart: () => [s('path', o('#ff4f7b', { d: 'M16 28C4 20 2 13 5 8.5 8 4 13 5 16 9.5 19 5 24 4 27 8.5c3 4.5 1 11.5-11 19.5z' })), shine('M8 10c1-2 3-2.5 4.5-1.5')],
  flame: () => [
    s('path', o('#ff6a3d', { d: 'M16 2c2 6 9 9 9 17a9 9 0 0 1-18 0c0-5 3-7 4-11 1 3 3 4 3 4 0-4 1-7 2-10z' })),
    s('path', o('#ffc83d', { d: 'M16 15c1 3 4 4 4 8a4 4 0 0 1-8 0c0-3 2-4 4-8z', 'stroke-width': 1.6 })),
  ],
  snow: () => {
    const arms = 'M16 3v26M4.7 9.5l22.6 13M4.7 22.5l22.6-13M13 5.5l3 3 3-3M13 26.5l3-3 3 3';
    return [s('path', { d: arms, fill: 'none', stroke: STROKE, 'stroke-width': 5, 'stroke-linecap': 'round', 'stroke-linejoin': 'round' }), s('path', { d: arms, fill: 'none', stroke: '#bfeaff', 'stroke-width': 2.4, 'stroke-linecap': 'round', 'stroke-linejoin': 'round' })];
  },
  petal: () => {
    const out: SVGElement[] = [];
    for (let i = 0; i < 5; i++) out.push(s('ellipse', o('#ffb3d1', { cx: 16, cy: 8.5, rx: 5, ry: 7, transform: `rotate(${i * 72} 16 16)`, 'stroke-width': 2 })));
    out.push(s('circle', o('#ffc83d', { cx: 16, cy: 16, r: 4, 'stroke-width': 1.8 })));
    return out;
  },
  note: () => [
    s('path', { d: 'M13.5 24V7.5l14-3.5v17', fill: 'none', stroke: STROKE, 'stroke-width': 3, 'stroke-linejoin': 'round' }),
    s('ellipse', o('#b26bff', { cx: 9.5, cy: 24.5, rx: 5, ry: 4, transform: 'rotate(-18 9.5 24.5)' })),
    s('ellipse', o('#b26bff', { cx: 23.5, cy: 21.5, rx: 5, ry: 4, transform: 'rotate(-18 23.5 21.5)' })),
  ],
  bubble: () => [
    s('circle', o('#d9f4ff', { cx: 14, cy: 17, r: 11 })),
    s('circle', o('#d9f4ff', { cx: 26, cy: 6.5, r: 3.6, 'stroke-width': 1.8 })),
    shine('M8 13.5a7 7 0 0 1 4.5-4.5'),
  ],

  // ---- comportements d'arme
  dot: () => [s('circle', o('#ffc83d', { cx: 16, cy: 16, r: 8 })), shine('M12.5 13a4 4 0 0 1 2.5-2.5')],
  pierce: () => [
    line('M6 26L24 8', 3.2),
    s('polygon', o('#ff4f7b', { points: '28.5,3.5 26,13 19,6' })),
    s('path', o('#3dd6ff', { d: 'M4 21l5 2 2 5-4.5-1.5z', 'stroke-width': 1.8 })),
  ],
  shotgun: () => [
    s('circle', o('#ffc83d', { cx: 8, cy: 9, r: 4.5 })),
    s('circle', o('#ffc83d', { cx: 24, cy: 9, r: 4.5 })),
    s('circle', o('#ffc83d', { cx: 16, cy: 15, r: 4.5 })),
    s('circle', o('#ffc83d', { cx: 10, cy: 23, r: 4.5 })),
    s('circle', o('#ffc83d', { cx: 22, cy: 23, r: 4.5 })),
  ],
  missile: () => [
    s('path', o('#ffc83d', { d: 'M12.5 21h7l-3.5 9z', 'stroke-width': 1.8 })),
    s('polygon', o('#ff4f7b', { points: '10.5,15 5,23 11,22' })),
    s('polygon', o('#ff4f7b', { points: '21.5,15 27,23 21,22' })),
    s('path', o('#e9eef7', { d: 'M16 2c5 4 6 10 6 19H10c0-9 1-15 6-19z' })),
    s('circle', o('#3dd6ff', { cx: 16, cy: 11.5, r: 2.6, 'stroke-width': 1.6 })),
  ],
  comet: () => [
    s('path', { d: 'M13 16L27 2M16 20L30 9M10 13L20 1', fill: 'none', stroke: STROKE, 'stroke-width': 5.4, 'stroke-linecap': 'round' }),
    s('path', { d: 'M13 16L27 2M16 20L30 9M10 13L20 1', fill: 'none', stroke: '#ff6a3d', 'stroke-width': 2.8, 'stroke-linecap': 'round' }),
    s('circle', o('#ffc83d', { cx: 10.5, cy: 21.5, r: 7.5 })),
    shine('M7 19a4.5 4.5 0 0 1 3-3'),
  ],
  globe: () => [
    s('circle', o('#5cc8ff', { cx: 16, cy: 16, r: 12.5 })),
    s('path', o('#7be08a', { d: 'M8 10c3-2 6-1 7 2s-3 4-2 7-4 3-5 0-3-6 0-9zM19 8c3-1 6 0 8 3-2 1-4 0-5 2s-3 1-3-5z', 'stroke-width': 1.6 })),
    line('M16 3.5c-5 4-5 21 0 25M16 3.5c5 4 5 21 0 25', 1.4),
    shine('M9 9a9 9 0 0 1 5-4'),
  ],
  chat: () => [
    s('path', o('#ffffff', { d: 'M5 5h22a3 3 0 0 1 3 3v12a3 3 0 0 1-3 3H15l-6 6v-6H5a3 3 0 0 1-3-3V8a3 3 0 0 1 3-3z' })),
    s('circle', { cx: 10, cy: 14, r: 1.8, fill: STROKE }),
    s('circle', { cx: 16, cy: 14, r: 1.8, fill: STROKE }),
    s('circle', { cx: 22, cy: 14, r: 1.8, fill: STROKE }),
  ],
  emote: () => [
    s('circle', o('#ffd25e', { cx: 16, cy: 16, r: 13 })),
    s('circle', { cx: 11.5, cy: 13, r: 2, fill: STROKE }),
    s('circle', { cx: 20.5, cy: 13, r: 2, fill: STROKE }),
    line('M9.5 19c2 4.5 11 4.5 13 0', 2.4),
    shine('M7.5 9.5a9.5 9.5 0 0 1 4-3.5'),
  ],
  people: () => [
    s('circle', o('#ffc89a', { cx: 11, cy: 11, r: 5 })),
    s('path', o('#5cc8ff', { d: 'M2 28c0-6 4-10 9-10s9 4 9 10z' })),
    s('circle', o('#ffd9b8', { cx: 22, cy: 12, r: 4.2, 'stroke-width': 2 })),
    s('path', o('#ff9bb8', { d: 'M17 28c.5-5 3-8 7-8s6 3 6 8z', 'stroke-width': 2 })),
  ],
  trophy: () => [
    s('path', o('#ffc83d', { d: 'M9 4h14v9a7 7 0 0 1-14 0z' })),
    s('path', { d: 'M9 7H4c0 6 2 9 6 9M23 7h5c0 6-2 9-6 9', fill: 'none', stroke: STROKE, 'stroke-width': 2.4, 'stroke-linecap': 'round' }),
    s('rect', o('#e2a82e', { x: 13.5, y: 19.5, width: 5, height: 5 })),
    s('rect', o('#ffc83d', { x: 9, y: 24.5, width: 14, height: 4, rx: 1.5 })),
    shine('M12 7v6'),
  ],
  mail: () => [
    s('rect', o('#ffffff', { x: 3, y: 7, width: 26, height: 18, rx: 3 })),
    line('M4 9l12 9 12-9', 2.4),
    shine('M6 22l5-4'),
  ],
  crown: () => [
    s('path', o('#ffc83d', { d: 'M4 24L3 9l7 6 6-10 6 10 7-6-1 15z' })),
    s('rect', o('#e2a82e', { x: 4, y: 24, width: 24, height: 4, rx: 1.5 })),
    s('circle', { cx: 16, cy: 18, r: 2, fill: '#ff4f7b', stroke: STROKE, 'stroke-width': 1.4 }),
  ],
};

/** Icone d'interface (SVG 32x32) ; `cls` = classe CSS (taille pilotee par le conteneur, 1em par defaut). */
export const uiIcon = (id: UiIconId, cls = 'ui-ico'): SVGSVGElement => {
  const svg = document.createElementNS(NS, 'svg');
  svg.setAttribute('viewBox', '0 0 32 32');
  svg.setAttribute('class', cls);
  svg.setAttribute('aria-hidden', 'true');
  svg.setAttribute('focusable', 'false');
  for (const k of SHAPES[id]()) svg.append(k);
  return svg;
};
